import { Panel, Text, Title, Icon, ButtonLink, Skeleton } from "components/commons";
import { TransactionType, PaymentMethod } from "enums";
import { Path } from "paths";
import React, { useMemo } from "react";
import lang from "translations";
import classnames from "classnames";
import { v4 as uuidv4 } from "uuid";
import { Popover } from "antd";

/**
 * Displays the transaction receipt summary using the backend-calculated
 * `soarSummary` (built from `transaction.pricingModel` in the
 * `viewTransactions` mapper). This component performs no pricing
 * calculations of its own - it only maps/formats the values the backend
 * already computed onto the wireframes (Receipt Wireframes 1d - whole
 * check discount mode, and 1h - per item discount mode).
 */
const TransactionSummarySoar = ({ data = {}, loading }) => {
  const { soarSummary, note, paymentMethod } = data;
  const isPaidByCashOrCard = [
    PaymentMethod.Cash,
    PaymentMethod.CreditDebit,
    PaymentMethod.Credit,
    PaymentMethod.EWallet,
    PaymentMethod.Others,
  ].includes(paymentMethod);
  const showBalances = !(data.type === TransactionType.SALE && isPaidByCashOrCard);
  const isTypeWithPaymentInfo =
    data.type !== TransactionType.REDEEM &&
    ![TransactionType.VOUCHER_ISSUE, TransactionType.VOUCHER_REMOVE].includes(data.type);
  const showPreviousBalance = isTypeWithPaymentInfo && data.previousBalance && showBalances;
  const showTotalPaid = isTypeWithPaymentInfo && data.totalPaid;
  const showNewBalance = isTypeWithPaymentInfo && data.newBalance && showBalances;

  const outletInfo = useMemo(() => {
    if (!soarSummary) {
      return null;
    }
    const { locationName } = soarSummary.location || {};
    const { tableName } = soarSummary.table || {};
    return (
      <div className="mb-md flex items-center">
        <span className="flex h-10 w-10 flex-none items-center justify-center rounded bg-blue-opaque">
          <Icon name="vendor" className="text-gray" />
        </span>
        <span className="ml-md flex flex-col">
          <span className="text-xs font-semibold uppercase text-gray">{lang.outlet}</span>
          <span className="text-md font-bold text-black">{locationName || "-"}</span>
        </span>
        {tableName && (
          <>
            <span className="mx-md h-10 border-l border-gray-border" />
            <span className="flex flex-col">
              <span className="text-xs font-semibold uppercase text-gray">{lang.table}</span>
              <span className="inline-flex items-center rounded border border-gray-border bg-blue-opaque px-sm py-xs text-sm font-semibold leading-none text-black-light">
                {tableName}
              </span>
            </span>
          </>
        )}
      </div>
    );
  }, [soarSummary]);

  const itemRows = useMemo(() => {
    if (!soarSummary?.rows?.length) {
      return null;
    }
    return (
      <div>
        <div className="grid grid-cols-12 gap-1 pb-sm border-b border-gray-lightest">
          <div className="col-span-6">
            <Text uppercase strong color="text-gray" size="text-xs">
              {lang.items}
            </Text>
          </div>
          <div className="col-span-3 text-right">
            <Text uppercase strong color="text-gray" size="text-xs">
              {`${lang.qty} × ${lang.unitPrice}`}
            </Text>
          </div>
          <div className="col-span-1 text-right">
            <Text uppercase strong color="text-gray" size="text-xs">
              {lang.tax}
            </Text>
          </div>
          <div className="col-span-2 text-right">
            <Text uppercase strong color="text-gray" size="text-xs">
              {lang.amount}
            </Text>
          </div>
        </div>
        {soarSummary.rows.map((row) => {
          if (row.kind === "itemDiscount") {
            return (
              <div
                key={row.key || uuidv4()}
                className="grid grid-cols-12 gap-1 py-sm bg-yellow-lighter"
              >
                <div className="col-span-6 pl-md">
                  <Text size="text-sm" color="text-yellow-darkest">
                    {row.name}
                  </Text>
                </div>
                <div className="col-span-3 text-right">
                  <Text size="text-sm" color="text-yellow-darkest">
                    {row.amount}
                  </Text>
                </div>
                <div className="col-span-1 text-right">
                  <Text size="text-sm" color="text-yellow-darkest">
                    {row.taxPercentage}
                  </Text>
                </div>
                <div className="col-span-2"></div>
              </div>
            );
          }
          return (
            <div
              key={row.key || uuidv4()}
              className="grid grid-cols-12 gap-1 py-sm border-b border-gray-lightest"
            >
              <div className="col-span-6 flex items-center">
                <Text strong={row.kind === "item"}>{row.name}</Text>
                {row.note && (
                  <Popover
                    content={<Text size="text-sm">{row.note}</Text>}
                    trigger="click"
                    placement="top"
                  >
                    <span className="ml-xs inline-flex cursor-pointer" style={{ color: "#00696d" }}>
                      <Icon name="info" fontSize="10px" />
                    </span>
                  </Popover>
                )}
                {row.kind === "voucher" && (
                  <Popover
                    content={<Text size="text-sm">{lang.includedServiceCharge}</Text>}
                    trigger="click"
                    placement="top"
                  >
                    <span className="ml-xs inline-flex cursor-pointer" style={{ color: "#00696d" }}>
                      <Icon name="info" fontSize="10px" />
                    </span>
                  </Popover>
                )}
              </div>
              <div className="col-span-3 text-right">
                <Text color="text-gray">
                  {row.kind === "voucher"
                    ? `${row.quantity} × (- ${row.voucherUnitPrice})`
                    : `${row.quantity} × ${row.unitPrice}`}
                </Text>
              </div>
              <div className="col-span-1 text-right">
                <Text color="text-gray">{row.taxPercentage}</Text>
              </div>
              <div className="col-span-2 text-right">
                {row.kind !== "voucher" && <Text strong>{row.amount}</Text>}
              </div>
            </div>
          );
        })}
      </div>
    );
  }, [soarSummary]);

  const notesSection = useMemo(() => {
    if (!note) {
      return null;
    }
    return (
      <div className="mt-lg flex border p-md rounded">
        <div className="mr-lg">
          <Icon name="double-quote" className="text-gray-lightest" fontSize="30px" />
        </div>
        <div>
          <Text className="text-gray font-semibold mb-md" uppercase strong size="text-xs">
            {lang.notes}
          </Text>
          <Text italic>{note}</Text>
        </div>
      </div>
    );
  }, [note]);

  const summaryRow = (label, value, options = {}) => {
    const { strong = false } = options;
    return (
      <div
        className="flex items-center justify-between mt-sm"
        key={typeof label === "string" ? label : uuidv4()}
      >
        <Text
          size={strong ? "text-md" : "text-sm"}
          strong={strong}
          color={strong ? undefined : "text-gray"}
        >
          {label}
        </Text>
        <Text size={strong ? "text-md" : "text-sm"} strong={strong}>
          {value}
        </Text>
      </div>
    );
  };

  const summaryRows = useMemo(() => {
    if (!soarSummary) {
      return null;
    }
    return (
      <div className="mt-md pt-md border-t border-gray-lightest flex justify-end">
        <div className="w-full md:w-1/2 lg:w-2/5">
          {summaryRow(lang.subTotal, soarSummary.subtotal, { strong: true })}
          {summaryRow(
            `${lang.serviceCharge} (${soarSummary.serviceChargePercentage})`,
            soarSummary.serviceChargeAmount,
          )}
          {soarSummary.hasVoucher && summaryRow(lang.vouchers, `-${soarSummary.voucherAmount}`)}
          {soarSummary.hasDiscount &&
            summaryRow(soarSummary.discountLabel, `-${soarSummary.discountAmount}`)}
          {summaryRow(
            <>
              {lang.netValue}
              <sup>(*)</sup>
            </>,
            soarSummary.netValue,
            { strong: true },
          )}
          {summaryRow(lang.tax, soarSummary.taxAmount)}
          {summaryRow(lang.tip, soarSummary.tipAmount)}
          <div className="mt-md pt-md border-t-2 border-black flex items-center justify-between">
            <Title>{lang.totalDue}</Title>
            <Title>{soarSummary.totalDue}</Title>
          </div>
          <Text italic size="text-xs" color="text-gray" className="mt-md text-left">
            {lang.netValueFootnote}
          </Text>
        </div>
      </div>
    );
  }, [soarSummary]);

  if (loading) {
    return (
      <Panel>
        <Skeleton />
      </Panel>
    );
  }

  if (!soarSummary) {
    return null;
  }

  return (
    <>
      <Panel>
        {outletInfo}
        {itemRows}
        {summaryRows}
        {notesSection}
      </Panel>

      {(showPreviousBalance ||
        showTotalPaid ||
        showNewBalance ||
        data.updatedFromTransactionId) && (
        <Panel>
          <div>
            <Title>{lang.paymentInfo}</Title>
          </div>

          {showPreviousBalance && (
            <>
              <div className="-mr-lg -ml-lg mt-md border border-bottom border-gray-lightest"></div>
              {data.previousBalance.map(
                ([name, amount, styles = {}, titleClass = "", amountClass = ""]) => {
                  const {
                    fontSize = "text-sm",
                    danger = false,
                    strong = false,
                    strongTitle = false,
                  } = styles;
                  return (
                    <div className="grid grid-cols-4 md:gap-0 gap-1" key={uuidv4()}>
                      <div className="md:col-span-3 col-span-4 flex items-center">
                        <Text
                          size="text-sm"
                          className={classnames(titleClass, { "font-semibold": strongTitle })}
                        >
                          {name}
                        </Text>
                      </div>
                      <div className="md:col-span-1 col-span-4 md:text-right">
                        <Text
                          strong={strong}
                          size={fontSize}
                          danger={danger}
                          className={classnames(amountClass, { "font-semibold": strongTitle })}
                        >
                          {amount}
                        </Text>
                      </div>
                    </div>
                  );
                },
              )}
            </>
          )}

          {showTotalPaid && (
            <>
              <div className="-mr-lg -ml-lg mt-md border border-bottom border-gray-lightest"></div>
              {data.totalPaid.map(
                ([name, amount, styles = {}, titleClass = "", amountClass = ""]) => {
                  const {
                    fontSize = "text-sm",
                    danger = false,
                    strong = false,
                    lineThrough = false,
                  } = styles;
                  return (
                    <div className="flex items-center justify-between" key={uuidv4()}>
                      <div>
                        <Text
                          size={fontSize}
                          className={classnames(titleClass, { "line-through": lineThrough })}
                        >
                          {name}
                        </Text>
                      </div>
                      <div>
                        <Text
                          strong={strong}
                          size={fontSize}
                          danger={danger}
                          className={classnames(amountClass, { "line-through": lineThrough })}
                        >
                          {amount}
                        </Text>
                      </div>
                    </div>
                  );
                },
              )}
            </>
          )}

          {showNewBalance && (
            <>
              <div className="-mr-lg -ml-lg mt-md border border-bottom border-gray-lightest"></div>
              {data.newBalance.map(
                ([name, amount, styles = {}, titleClass = "", amountClass = ""]) => {
                  const {
                    fontSize = "text-sm",
                    danger = false,
                    strong = false,
                    strongTitle = false,
                    superscript = "",
                  } = styles;
                  return (
                    <div className="grid grid-cols-4 md:gap-0 gap-1" key={uuidv4()}>
                      <div className="md:col-span-3 col-span-4 flex items-center">
                        <Text
                          size="text-sm"
                          className={classnames(titleClass, { "font-semibold": strongTitle })}
                        >
                          {name}
                          <sup>{superscript}</sup>
                        </Text>
                      </div>
                      <div className="md:col-span-1 col-span-4 md:text-right">
                        <Text
                          strong={strong}
                          size={fontSize}
                          danger={danger}
                          className={classnames(amountClass, { "font-semibold": strongTitle })}
                        >
                          {amount}
                        </Text>
                      </div>
                    </div>
                  );
                },
              )}
            </>
          )}

          {data.updatedFromTransactionId && (
            <div className="mt-lg flex border p-md rounded">
              <div className="mr-lg">
                <Icon name="info" className="text-foreground" fontSize="30px" />
              </div>
              <div>
                <Text className="text-gray font-semibold mb-sm" uppercase strong size="text-xs">
                  {lang.transferFrom}
                </Text>
                <div>
                  <ButtonLink
                    className="text-blue"
                    newTabPath={Path.TRANSACTION_ID(data.updatedFromTransactionId)}
                  >
                    #{data.updatedFromTransactionId}
                  </ButtonLink>
                </div>
              </div>
            </div>
          )}
        </Panel>
      )}

      {data.isVoided && (
        <Panel>
          {data.type !== TransactionType.REDEEM && (
            <>
              <div>
                <Title>{lang.voidInfo}</Title>
              </div>
              {data.voidedPreviousBalance && showBalances
                ? data.voidedPreviousBalance.map(([name, amount, nameClass, amountClass]) => {
                    return (
                      <div className="grid grid-cols-4 md:gap-4 gap-0" key={uuidv4()}>
                        <div className="md:col-span-3 col-span-4 flex items-center">
                          <Text className={classnames(nameClass)}>{name}</Text>
                        </div>
                        <div className="md:col-span-1 col-span-4 md:text-right">
                          <Text className={classnames(amountClass)}>{amount}</Text>
                        </div>
                      </div>
                    );
                  })
                : null}

              <div
                className={`-mr-lg -ml-lg mt-md ${
                  showBalances ? "border" : ""
                } border-bottom border-gray-lightest`}
              ></div>
              {data.voidTotalPaid?.map(
                ([name, amount, styles = {}, titleClass = "", amountClass = ""]) => {
                  const { fontSize = "text-sm", danger = false, strong = false } = styles;

                  return (
                    <div className={classnames("grid grid-cols-4 md:gap-0 gap-1")} key={uuidv4()}>
                      <div className="md:col-span-3 col-span-4 flex items-center">
                        <Text className={classnames(titleClass)}>{name}</Text>
                      </div>
                      <div className="md:col-span-1 col-span-4 md:text-right">
                        <Text
                          strong={strong}
                          size={fontSize}
                          danger={danger}
                          className={classnames(amountClass)}
                        >
                          {amount}
                        </Text>
                      </div>
                    </div>
                  );
                },
              )}
              <div
                className={`-mr-lg -ml-lg mt-md ${
                  showBalances ? "border" : ""
                } border-bottom border-gray-lightest`}
              ></div>

              {data.voidedNewBalance && showBalances
                ? data.voidedNewBalance.map(([name, amount, nameClass, amountClass]) => {
                    return (
                      <div className="grid grid-cols-4 md:gap-4 gap-0" key={uuidv4()}>
                        <div className="md:col-span-3 col-span-4 flex items-center">
                          <Text className={classnames(nameClass)}>{name}</Text>
                        </div>
                        <div className="md:col-span-1 col-span-4 md:text-right">
                          <Text className={classnames(amountClass)}>{amount}</Text>
                        </div>
                      </div>
                    );
                  })
                : null}
            </>
          )}

          {data.voidReason && (
            <div className="mt-lg flex border p-md rounded">
              <div className="mr-lg">
                <Icon name="info" className="text-foreground" fontSize="30px" />
              </div>
              <div>
                <Text className="text-gray font-semibold mb-md" uppercase strong size="text-xs">
                  {lang.voidReason}
                </Text>
                <Text italic>{data.voidReason}</Text>
              </div>
            </div>
          )}

          {data.newTransactionId && (
            <div className="mt-lg flex border p-md rounded">
              <div className="mr-lg">
                <Icon name="info" className="text-foreground" fontSize="30px" />
              </div>
              <div>
                <Text className="text-gray font-semibold mb-sm" uppercase strong size="text-xs">
                  {lang.transferTo}
                </Text>
                <div>
                  <ButtonLink
                    className="text-blue"
                    newTabPath={Path.TRANSACTION_ID(data.newTransactionId)}
                  >
                    #{data.newTransactionId}
                  </ButtonLink>
                </div>
              </div>
            </div>
          )}
        </Panel>
      )}
    </>
  );
};

export default TransactionSummarySoar;
